import { createClient, type SupabaseClient } from "@supabase/supabase-js"
import type { Adjustment, Balance, Bet, LedgerData, LedgerEntry, OwnerTotal, Partner, Profile, Unit, Workspace } from "../types"

const url = import.meta.env.VITE_SUPABASE_URL
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

export const isConfigured = Boolean(url && key)

export const supabase: SupabaseClient | null = isConfigured
  ? createClient(url!, key!, { auth: { persistSession: true, autoRefreshToken: true } })
  : null

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.")
  return supabase
}

export async function loadLedger(workspaceId: string): Promise<LedgerData> {
  const client = requireClient()
  const { data: authData, error: authError } = await client.auth.getUser()
  if (authError || !authData.user) throw new Error("Your session has expired.")
  const [workspace, units, partners, bets, adjustments, entries, balances, ownerTotals, profile, auditEvents] = await Promise.all([
    client.from("workspaces").select("id, owner_id, name, archived_at").eq("id", workspaceId).single(),
    client.from("settlement_units").select("id, code, label, symbol, position, archived_at").eq("workspace_id", workspaceId).order("position"),
    client.from("partners").select("id, name, archived_at").eq("workspace_id", workspaceId).order("name"),
    client.from("bets").select("id, kind, status, unit_code, total_amount, owner_amount, odds, description, source_url, split_partner_id, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    client.from("adjustments").select("id, partner_id, unit_code, amount, notes, reversed_at, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    client.from("ledger_entries").select("id, partner_id, unit_code, amount, kind, notes, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }).limit(30),
    client.rpc("workspace_dashboard", { target_workspace: workspaceId }),
    client.rpc("workspace_owner_totals", { target_workspace: workspaceId }),
    client.from("profiles").select("id, email, display_name, role").eq("id", authData.user.id).single(),
    client.from("audit_events").select("id, actor_id, workspace_id, action, metadata, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }).limit(200),
  ])

  const betIds = (bets.data || []).map((bet) => bet.id)
  const allocations = betIds.length
    ? await client.from("bet_allocations").select("id, bet_id, partner_id, amount").in("bet_id", betIds)
    : { data: [], error: null }
  const failed = [workspace, units, partners, bets, adjustments, entries, balances, ownerTotals, profile, auditEvents, allocations].find((result) => result.error)
  if (failed?.error) throw new Error(failed.error.message)
  return {
    profile: profile.data as Profile,
    workspace: workspace.data as Workspace,
    units: (units.data || []) as Unit[],
    partners: (partners.data || []) as Partner[],
    bets: (bets.data || []).map((bet) => ({
      ...bet,
      allocations: (allocations.data || []).filter((allocation) => allocation.bet_id === bet.id),
    })) as Bet[],
    adjustments: (adjustments.data || []) as Adjustment[],
    entries: (entries.data || []) as LedgerEntry[],
    auditEvents: (auditEvents.data || []) as LedgerData["auditEvents"],
    balances: (balances.data || []) as Balance[],
    ownerTotals: (ownerTotals.data || []) as OwnerTotal[],
  }
}

export async function myWorkspaceId() {
  const client = requireClient()
  const { data: authData, error: authError } = await client.auth.getUser()
  if (authError || !authData.user) throw new Error("Your session has expired.")
  const { data: profile, error: profileError } = await client.from("profiles").select("id").eq("id", authData.user.id).single()
  if (profileError || !profile) throw new Error(profileError?.message || "Your profile is unavailable.")
  const { data, error } = await client.from("workspaces").select("id").eq("owner_id", profile.id).single()
  if (error || !data) throw new Error(error?.message || "Your ledger has not been provisioned.")
  return data.id
}

export async function callLedgerRpc(name: string, params: Record<string, unknown>) {
  const client = requireClient()
  const { data, error } = await client.rpc(name, params)
  if (error) throw new Error(error.message)
  return data
}

export async function downloadSnapshot(workspaceId: string, filename: string) {
  const snapshot = await callLedgerRpc("workspace_snapshot", { target_workspace: workspaceId })
  const url = URL.createObjectURL(new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" }))
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
